/**
 * Agenda du tableau de bord : les tâches de l'équipe et les échéances, vues
 * au jour, à la semaine ou au mois.
 *
 * À quoi ça sert. Une tâche se confie depuis la fiche d'une entreprise
 * (bouton « Tâche ») en désignant la personne qui s'en charge. Elle apparaît
 * ici, pour tout le monde : le patron confie une action sans passer par un
 * appel ou un texto, et chacun voit où en est l'équipe. Tout le monde voit
 * tout — une tâche que personne ne regarde est une tâche oubliée.
 *
 * Qui peut quoi. N'importe qui crée une tâche, pour soi ou pour un autre.
 * Seul un administrateur la clôture, la modifie ou la supprime. Le bouton
 * « Clôturer » n'apparaît donc que pour eux — mais c'est la route serveur qui
 * refuse, pas le bouton : masquer un bouton ne protège rien.
 *
 * Pourquoi trois vues. Le mois sert à repérer les périodes chargées, la
 * semaine à organiser, le jour à travailler. Une seule vue oblige à choisir
 * entre les trois usages ; les onglets coûtent moins cher que ce compromis.
 *
 * Le détail est toujours sous le calendrier, jamais dans les cases : une case
 * de calendrier ne peut pas porter le titre, l'entreprise, le destinataire et
 * un bouton sans devenir illisible. On clique un jour, on lit ce qu'il porte.
 *
 * Rien ne se crée ici. Une tâche naît dans la fiche d'une entreprise, où l'on
 * dit à qui elle revient et sur quelle société elle porte — deux choses qu'un
 * champ de saisie posé sur le tableau de bord ne demandait pas, et qui
 * manquaient ensuite à la lecture.
 *
 * Aucune table nouvelle : tout vit dans l'agenda existant. Une tâche apparaît
 * donc aussi dans l'écran Agenda, et un rendez-vous pris là-bas apparaît ici.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { C, E, RAYON, T, bouton, pastille } from '../theme';
import { API } from '../api';
import Carte from './Carte';

// Les couleurs des types sont celles de l'écran Agenda. Les deux écrans lisent
// la même table : un type connu là-bas et inconnu ici s'afficherait sans
// couleur, et inversement.
const TYPE_COULEURS = {
  RDV:     { fond: '#dbeafe', texte: '#1d4ed8', emoji: '📅' },
  Relance: { fond: '#fef3c7', texte: '#92400e', emoji: '🔔' },
  Appel:   { fond: '#d1fae5', texte: '#065f46', emoji: '📞' },
  'Tâche': { fond: '#ede9fe', texte: '#5b21b6', emoji: '✅' },
};

const JOURS_COURTS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
const MOIS_FR = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet',
                 'août', 'septembre', 'octobre', 'novembre', 'décembre'];

const MODES = [
  { cle: 'jour', libelle: 'Jour' },
  { cle: 'semaine', libelle: 'Semaine' },
  { cle: 'mois', libelle: 'Mois' },
];

/** Minuit, pour comparer des jours sans que l'heure s'en mêle. */
function aMinuit(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

/** Clé de regroupement par jour, en heure locale — `toISOString` décalerait. */
function cleJour(date) {
  const d = new Date(date);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

/** Lundi de la semaine : en France la semaine commence le lundi, pas le dimanche. */
function lundiDe(date) {
  const d = aMinuit(date);
  const decalage = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - decalage);
  return d;
}

function ajouterJours(date, n) {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

/** Les jours affichés, et le libellé de la période, pour le mode demandé. */
function calculerPeriode(mode, ancre) {
  if (mode === 'jour') {
    const jour = aMinuit(ancre);
    return {
      jours: [jour],
      libelle: `${jour.getDate()} ${MOIS_FR[jour.getMonth()]} ${jour.getFullYear()}`,
    };
  }
  if (mode === 'semaine') {
    const lundi = lundiDe(ancre);
    const jours = Array.from({ length: 7 }, (_, i) => ajouterJours(lundi, i));
    const fin = jours[6];
    const memeMois = lundi.getMonth() === fin.getMonth();
    return {
      jours,
      libelle: memeMois
        ? `${lundi.getDate()} – ${fin.getDate()} ${MOIS_FR[fin.getMonth()]}`
        : `${lundi.getDate()} ${MOIS_FR[lundi.getMonth()]} – ${fin.getDate()} ${MOIS_FR[fin.getMonth()]}`,
    };
  }
  // Mois : la grille commence au lundi qui précède le 1er et se termine au
  // dimanche qui suit le dernier jour, sinon les semaines sont tronquées.
  const premier = new Date(ancre.getFullYear(), ancre.getMonth(), 1);
  const dernier = new Date(ancre.getFullYear(), ancre.getMonth() + 1, 0);
  const debut = lundiDe(premier);
  const jours = [];
  for (let d = new Date(debut); d <= dernier || jours.length % 7 !== 0; d = ajouterJours(d, 1)) {
    jours.push(new Date(d));
  }
  return {
    jours,
    libelle: `${MOIS_FR[ancre.getMonth()]} ${ancre.getFullYear()}`,
  };
}

function Etiquette({ evenement, compact }) {
  const type = TYPE_COULEURS[evenement.type] || TYPE_COULEURS.RDV;
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '4px',
      background: type.fond, color: type.texte,
      borderRadius: RAYON.sm, padding: compact ? '1px 5px' : '3px 7px',
      fontSize: compact ? '10.5px' : '11.5px', fontWeight: 600,
      overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis',
      maxWidth: '100%',
    }}>
      <span>{type.emoji}</span>
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{evenement.titre}</span>
    </div>
  );
}

function Ligne({ evenement: e, user, estAdmin, aujourdhui, onCloturer, avecDate }) {
  const type = TYPE_COULEURS[e.type] || TYPE_COULEURS.RDV;
  const enRetard = aMinuit(e.date_debut) < aujourdhui;
  const quand = new Date(e.date_debut);
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      gap: E.md, padding: `${E.md} ${E.lg}`,
      borderTop: `1px solid ${C.bordure}`,
    }}>
      <div style={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: E.sm }}>
        <span style={{ fontSize: '13px' }} title={e.type}>{type.emoji}</span>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: '13.5px', fontWeight: 600, color: C.texte,
                        overflow: 'hidden', textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap' }}>
            {e.titre}
          </div>
          <div style={{ marginTop: '2px', display: 'flex', alignItems: 'center',
                        gap: E.sm, flexWrap: 'wrap' }}>
            {/* La date n'est utile que hors calendrier : dans le détail d'un
                jour, elle est déjà écrite au-dessus. */}
            {avecDate && (
              <span style={pastille(enRetard ? 'danger' : 'neutre')}>
                {quand.getDate()} {MOIS_FR[quand.getMonth()]}
              </span>
            )}
            {/* L'entreprise n'apparaît que si la tâche part d'une fiche :
                beaucoup n'en visent aucune. */}
            {e.prospect_nom && <span style={pastille('info')}>{e.prospect_nom}</span>}
            {enRetard && !avecDate && <span style={pastille('danger')}>en retard</span>}
            {/* Qui doit faire cette tâche. Sans nom affiché, une tâche
                partagée par toute l'équipe n'engage personne. */}
            {e.attribue_a_nom ? (
              <span style={pastille(e.attribue_a_id === user?.id ? 'succes' : 'neutre')}>
                Pour {e.attribue_a_nom}
              </span>
            ) : (
              <span style={{ fontSize: '11.5px', color: C.texteFaible }}>
                attribuée à personne
              </span>
            )}
          </div>
        </div>
      </div>
      {estAdmin ? (
        <button onClick={() => onCloturer(e)}
                title="Déclarer cette ligne terminée"
                style={{ ...bouton('secondaire'), flexShrink: 0 }}>
          Clôturer
        </button>
      ) : (
        // Un non-admin voit la tâche mais ne la ferme pas. Le dire vaut mieux
        // qu'un vide qui ressemble à un bogue.
        <span style={{ fontSize: '11.5px', color: C.texteFaible, flexShrink: 0 }}>
          clôture par un admin
        </span>
      )}
    </div>
  );
}

export default function AgendaTableauBord({ evenements, user, onChangement, onVoirAgenda }) {
  const [mode, setMode] = useState('semaine');
  const [ancre, setAncre] = useState(() => aMinuit(new Date()));
  const [jourChoisi, setJourChoisi] = useState(() => aMinuit(new Date()));
  // « Toute l'équipe » ou « Mes tâches ». Le premier est le défaut : le bloc
  // existe d'abord pour que chacun voie ce que font les autres.
  const [filtre, setFiltre] = useState('equipe');
  const [erreur, setErreur] = useState('');
  // Tâche dont la clôture attend confirmation, ou null.
  const [aConfirmer, setAConfirmer] = useState(null);

  const estAdmin = user?.role === 'admin';
  const mesTaches = filtre === 'moi';
  const aujourdhui = aMinuit(new Date());

  // Une tâche clôturée sort du calendrier : elle garde sa trace en base (date
  // et auteur de la clôture), mais n'encombre plus l'écran.
  //
  // « Mes tâches » ne retient que ce qui m'est attribué. Une ligne sans
  // destinataire — un rendez-vous pris dans l'agenda, par exemple — n'est à
  // personne : elle sort de ma liste, sinon « mes tâches » ne voudrait rien
  // dire.
  const ouverts = evenements
    .filter(e => !e.cloturee_le)
    .filter(e => filtre === 'equipe' || e.attribue_a_id === user?.id);

  const parJour = {};
  ouverts.forEach(e => {
    const cle = cleJour(e.date_debut);
    (parJour[cle] = parJour[cle] || []).push(e);
  });
  Object.values(parJour).forEach(liste =>
    liste.sort((a, b) => new Date(a.date_debut) - new Date(b.date_debut)));

  // Compté sur tout l'agenda et non sur la période affichée : le badge doit
  // dire ce qui m'attend, pas ce qui tombe dans la semaine que je regarde.
  const mesTachesOuvertes = evenements.filter(
    e => !e.cloturee_le && e.attribue_a_id === user?.id).length;

  const { jours, libelle } = calculerPeriode(mode, ancre);

  // Le retard est ce qu'il faut voir en premier, quelle que soit la vue :
  // il est compté sur tout l'agenda, pas seulement sur la période affichée.
  const enRetard = ouverts.filter(e => aMinuit(e.date_debut) < aujourdhui).length;

  const naviguer = (sens) => {
    const pas = { jour: 1, semaine: 7, mois: 0 };
    if (mode === 'mois') {
      setAncre(new Date(ancre.getFullYear(), ancre.getMonth() + sens, 1));
    } else {
      setAncre(ajouterJours(ancre, pas[mode] * sens));
    }
  };

  const revenirAujourdhui = () => {
    setAncre(aujourdhui);
    setJourChoisi(aujourdhui);
  };

  const changerMode = (cle) => {
    setMode(cle);
    // Le jour lu reste le même d'une vue à l'autre : passer de la semaine au
    // mois ne doit pas faire perdre le jour qu'on était en train de regarder.
    setAncre(jourChoisi);
  };

  // Clôturer fait disparaître la ligne de tous les écrans : un clic de trop,
  // et la tâche s'évanouit sous les yeux de celui qui devait la faire. Un
  // admin peut la rouvrir, mais encore faut-il se souvenir de laquelle il
  // s'agissait — d'où la confirmation, qui la nomme.
  const cloturer = (evenement) => setAConfirmer(evenement);

  const confirmerCloture = () => {
    if (!aConfirmer) return;
    const id = aConfirmer.id;
    setAConfirmer(null);
    axios.patch(`${API}/agenda/${id}/cloture`, { cloturee: true })
      .then(onChangement)
      .catch(() => setErreur('La clôture a été refusée. Seul un administrateur peut clôturer une tâche.'));
  };

  const evenementsDuJour = parJour[cleJour(jourChoisi)] || [];

  const styleCase = (jour, hauteur) => {
    const estAujourdhui = cleJour(jour) === cleJour(aujourdhui);
    const estChoisi = cleJour(jour) === cleJour(jourChoisi);
    const horsMois = mode === 'mois' && jour.getMonth() !== ancre.getMonth();
    return {
      minHeight: hauteur,
      border: `1px solid ${estChoisi ? C.marine : C.bordure}`,
      borderRadius: RAYON.sm,
      padding: '5px',
      background: horsMois ? C.fond : C.surface,
      opacity: horsMois ? 0.55 : 1,
      cursor: 'pointer',
      display: 'flex', flexDirection: 'column', gap: '3px',
      minWidth: 0, overflow: 'hidden',
      boxShadow: estAujourdhui ? `inset 0 0 0 1px ${C.marine}` : 'none',
    };
  };

  return (
    <Carte
      titre="Agenda"
      precision={enRetard > 0 ? `${enRetard} échéance(s) en retard` : 'tâches et échéances de l\'équipe'}
      actions={
        // « Mes tâches » est une liste, pas un calendrier : les vues jour,
        // semaine et mois n'ont rien à y commander, et les laisser visibles
        // ferait croire qu'elles filtrent quelque chose.
        mesTaches ? null : (
        <div style={{ display: 'flex', background: C.fond, padding: '3px',
                      border: `1px solid ${C.bordure}`, borderRadius: RAYON.sm }}>
          {MODES.map(m => (
            <button key={m.cle} onClick={() => changerMode(m.cle)} style={{
              padding: '5px 12px', borderRadius: '4px', border: 'none',
              fontFamily: T.police, fontSize: '12.5px', cursor: 'pointer',
              fontWeight: mode === m.cle ? 600 : 500,
              background: mode === m.cle ? C.marine : 'transparent',
              color: mode === m.cle ? '#fff' : C.texteMoyen,
              transition: 'background 120ms ease',
            }}>{m.libelle}</button>
          ))}
        </div>
        )
      }
      sansPadding>

      {/* Navigation dans le temps */}
      <div style={{ display: 'flex', alignItems: 'center', gap: E.sm,
                    padding: `${E.md} ${E.lg}`, borderBottom: `1px solid ${C.bordure}`,
                    flexWrap: 'wrap' }}>
        {!mesTaches && (
          <>
            <button onClick={() => naviguer(-1)} style={bouton('secondaire')} aria-label="Période précédente">←</button>
            <button onClick={() => naviguer(1)} style={bouton('secondaire')} aria-label="Période suivante">→</button>
            <button onClick={revenirAujourdhui} style={bouton('discret')}>Aujourd'hui</button>
          </>
        )}
        <span style={{ fontSize: '14px', fontWeight: 600, color: C.texte,
                       textTransform: 'capitalize' }}>
          {mesTaches
            ? `${ouverts.length} tâche(s) à faire`
            : libelle}
        </span>
        {enRetard > 0 && (
          <span style={{ ...pastille('danger'), marginLeft: 'auto' }}>{enRetard} en retard</span>
        )}
        {/* Mes tâches : le tableau de bord montre tout par défaut, mais chacun
            doit pouvoir isoler ce qui lui revient sans lire les autres. */}
        <div style={{ display: 'flex', background: C.fond, padding: '3px',
                      border: `1px solid ${C.bordure}`, borderRadius: RAYON.sm,
                      marginLeft: enRetard > 0 ? 0 : 'auto' }}>
          {[{ cle: 'equipe', libelle: "Toute l'équipe" },
            { cle: 'moi', libelle: 'Mes tâches' }].map(f => (
            <button key={f.cle} onClick={() => setFiltre(f.cle)} style={{
              padding: '5px 12px', borderRadius: '4px', border: 'none',
              fontFamily: T.police, fontSize: '12.5px', cursor: 'pointer',
              fontWeight: filtre === f.cle ? 600 : 500,
              background: filtre === f.cle ? C.marine : 'transparent',
              color: filtre === f.cle ? '#fff' : C.texteMoyen,
              transition: 'background 120ms ease',
            }}>
              {f.libelle}
              {f.cle === 'moi' && mesTachesOuvertes > 0 && ` (${mesTachesOuvertes})`}
            </button>
          ))}
        </div>
      </div>

      {erreur && (
        <div style={{ padding: `${E.sm} ${E.lg}`, fontSize: '12.5px',
                      color: C.danger, background: C.dangerFond }}>
          {erreur}
        </div>
      )}

      {/* Le calendrier. Le mode « jour » n'a pas de grille : il n'aurait
          qu'une case, et la liste en dessous dit déjà tout. */}
      {!mesTaches && mode !== 'jour' && (
        <div style={{ padding: `${E.md} ${E.lg}` }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)',
                        gap: '4px', marginBottom: '4px' }}>
            {JOURS_COURTS.map(j => (
              <div key={j} style={{ ...T.etiquette, color: C.texteFaible,
                                    textAlign: 'center' }}>{j}</div>
            ))}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px' }}>
            {jours.map(jour => {
              const liste = parJour[cleJour(jour)] || [];
              const enRetardIci = liste.length > 0 && aMinuit(jour) < aujourdhui;
              return (
                <div key={jour.toISOString()} onClick={() => setJourChoisi(aMinuit(jour))}
                     style={styleCase(jour, mode === 'mois' ? 74 : 104)}>
                  <div style={{ fontSize: '11.5px', fontWeight: 600,
                                color: enRetardIci && liste.length ? C.danger : C.texteMoyen }}>
                    {jour.getDate()}
                  </div>
                  {liste.slice(0, mode === 'mois' ? 2 : 3).map(e => (
                    <Etiquette key={e.id} evenement={e} compact={mode === 'mois'} />
                  ))}
                  {liste.length > (mode === 'mois' ? 2 : 3) && (
                    <div style={{ fontSize: '10.5px', color: C.texteFaible }}>
                      +{liste.length - (mode === 'mois' ? 2 : 3)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Le détail du jour retenu : c'est ici qu'on lit l'entreprise, le
          destinataire, et qu'on clôture. */}
      {!mesTaches && (
      <div style={{ borderTop: mode !== 'jour' ? `1px solid ${C.bordure}` : 'none' }}>
        <div style={{ padding: `${E.md} ${E.lg} ${E.sm}`, ...T.etiquette, color: C.texteFaible }}>
          {cleJour(jourChoisi) === cleJour(aujourdhui)
            ? "Aujourd'hui"
            : `${jourChoisi.getDate()} ${MOIS_FR[jourChoisi.getMonth()]}`}
        </div>
        {evenementsDuJour.length === 0 ? (
          <div style={{ padding: `0 ${E.lg} ${E.lg}`, fontSize: '13px', color: C.texteFaible }}>
            Rien de prévu ce jour-là.
          </div>
        ) : (
          evenementsDuJour.map(e => (
            <Ligne key={e.id} evenement={e} user={user} estAdmin={estAdmin}
                   aujourdhui={aujourdhui} onCloturer={cloturer} />
          ))
        )}
      </div>
      )}

      {/* Mes tâches : tout ce qui m'est attribué, du plus urgent au plus
          lointain, sans avoir à chercher le bon jour dans le calendrier. */}
      {mesTaches && (
        ouverts.length === 0 ? (
          <div style={{ padding: `${E.xl} ${E.lg}`, fontSize: '13px',
                        color: C.texteFaible, textAlign: 'center' }}>
            Rien ne vous est attribué pour le moment.
          </div>
        ) : (
          ouverts
            .slice()
            .sort((a, b) => new Date(a.date_debut) - new Date(b.date_debut))
            .map(e => (
              <Ligne key={e.id} evenement={e} user={user} estAdmin={estAdmin}
                     aujourdhui={aujourdhui} onCloturer={cloturer} avecDate />
            ))
        )
      )}

      <div style={{ padding: `${E.sm} ${E.lg} ${E.md}`, borderTop: `1px solid ${C.bordure}` }}>
        <button onClick={onVoirAgenda} style={{ ...bouton('discret'), ...T.etiquette }}>
          Ouvrir l'agenda complet →
        </button>
      </div>

      {/* Confirmation de clôture. Elle nomme la tâche : « Clôturer ? » tout
          seul ne dit pas laquelle, et deux lignes se ressemblent vite. */}
      {aConfirmer && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      zIndex: 1000, padding: E.lg }}
             onClick={() => setAConfirmer(null)}>
          <div style={{ background: C.surface, borderRadius: RAYON.lg, width: 420,
                        maxWidth: '100%', boxShadow: '0 20px 60px rgba(16,24,40,0.24)' }}
               onClick={ev => ev.stopPropagation()}>
            <div style={{ padding: E.lg, borderBottom: `1px solid ${C.bordure}` }}>
              <h2 style={{ fontSize: '15px', fontWeight: 600, color: C.texte, margin: 0 }}>
                Clôturer cette tâche ?
              </h2>
            </div>
            <div style={{ padding: E.lg }}>
              <div style={{ fontSize: '14px', fontWeight: 600, color: C.texte }}>
                {aConfirmer.titre}
              </div>
              <div style={{ marginTop: E.xs, display: 'flex', gap: E.sm, flexWrap: 'wrap' }}>
                {aConfirmer.prospect_nom && (
                  <span style={pastille('info')}>{aConfirmer.prospect_nom}</span>
                )}
                {aConfirmer.attribue_a_nom && (
                  <span style={pastille('neutre')}>Pour {aConfirmer.attribue_a_nom}</span>
                )}
              </div>
              <p style={{ fontSize: '12.5px', color: C.texteFaible, margin: `${E.md} 0 0` }}>
                Elle disparaîtra de l'agenda et de la liste « Mes tâches ». Un
                administrateur pourra la rouvrir depuis l'écran Agenda.
              </p>
              <div style={{ display: 'flex', gap: E.sm, marginTop: E.lg }}>
                <button onClick={confirmerCloture}
                        style={{ ...bouton('principal'), flex: 1, justifyContent: 'center' }}>
                  Oui, clôturer
                </button>
                <button onClick={() => setAConfirmer(null)} style={bouton('secondaire')}>
                  Annuler
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Carte>
  );
}
